import { ref, computed } from "vue";

export interface DeltaMove {
  after_token: string | null;
  section_token?: string | null;
}

export interface SaveError {
  token: string;
  messages: string[];
}

export interface SaveErrorPayload {
  errors?: string[];
  error?: string;
}

type SaveRequest = PromiseLike<{
  response: { value: Response | null };
  data: { value: SaveErrorPayload | null };
  error: { value: unknown };
}>;

export const readErrorMessages = (payload: SaveErrorPayload | null, rawError: unknown): string[] => {
  if (payload?.errors?.length) return payload.errors;
  if (payload?.error) return [payload.error];
  if (Array.isArray(rawError)) return rawError.map(String);
  return rawError ? [String(rawError)] : [];
};

export interface SaveRequests {
  destroy: (token: string) => SaveRequest;
  create: (token: string) => SaveRequest;
  update: (token: string) => SaveRequest;
  reorder: (token: string) => SaveRequest;
}

export const useDeltaTracking = () => {
  const dirtyTokens = ref<Set<string>>(new Set());
  const newTokens = ref<Set<string>>(new Set());
  const deletedTokens = ref<string[]>([]);
  const moves = ref<Map<string, DeltaMove>>(new Map());

  const hasUnsavedChanges = computed(
    () =>
      dirtyTokens.value.size > 0 ||
      newTokens.value.size > 0 ||
      deletedTokens.value.length > 0 ||
      moves.value.size > 0,
  );

  const markNew = (token: string, move?: DeltaMove) => {
    newTokens.value.add(token);
    if (move) moves.value.set(token, move);
  };

  const markDirty = (token: string) => {
    if (!newTokens.value.has(token)) dirtyTokens.value.add(token);
  };

  const markDeleted = (token: string) => {
    if (newTokens.value.has(token)) newTokens.value.delete(token);
    else deletedTokens.value.push(token);
    dirtyTokens.value.delete(token);
    moves.value.delete(token);
  };

  const markMoved = (token: string, move: DeltaMove) => moves.value.set(token, move);

  const reset = () => {
    dirtyTokens.value = new Set();
    newTokens.value = new Set();
    deletedTokens.value = [];
    moves.value = new Map();
  };

  return { dirtyTokens, newTokens, deletedTokens, moves, hasUnsavedChanges, markNew, markDirty, markDeleted, markMoved, reset };
};

export const createSaveAll = (
  delta: ReturnType<typeof useDeltaTracking>,
  findIndex: (token: string) => number,
  requests: SaveRequests,
) => {
  const send = async (request: SaveRequest) => {
    const { response, data, error } = await request;
    const ok = response.value?.ok ?? false;
    const messages = ok ? [] : readErrorMessages(data.value, error.value);
    if (!ok && !messages.length) messages.push("Could not be saved");
    return { ok, status: response.value?.status ?? null, messages };
  };

  const destroyDeleted = async (errors: SaveError[]) => {
    await Promise.all(
      [...delta.deletedTokens.value].map(async (token) => {
        const result = await send(requests.destroy(token));
        if (result.ok || result.status === 404) {
          delta.deletedTokens.value = delta.deletedTokens.value.filter((t) => t !== token);
        } else errors.push({ token, messages: result.messages });
      }),
    );
  };

  const saveInDocumentOrder = async (errors: SaveError[]) => {
    const tokens = new Set([
      ...delta.newTokens.value,
      ...[...delta.moves.value.keys()].filter((token) => !delta.deletedTokens.value.includes(token)),
      ...[...delta.dirtyTokens.value].filter((token) => !delta.deletedTokens.value.includes(token)),
    ]);
    const operations = [...tokens].sort((a, b) => findIndex(a) - findIndex(b));
    for (const token of operations) {
      if (findIndex(token) === -1) {
        delta.dirtyTokens.value.delete(token);
        continue;
      }
      if (delta.newTokens.value.has(token)) {
        const result = await send(requests.create(token));
        if (!result.ok) {
          errors.push({ token, messages: result.messages });
          continue;
        }
        delta.newTokens.value.delete(token);
        delta.dirtyTokens.value.delete(token);
        delta.moves.value.delete(token);
        continue;
      }
      if (delta.moves.value.has(token)) {
        const result = await send(requests.reorder(token));
        if (result.ok) delta.moves.value.delete(token);
        else errors.push({ token, messages: result.messages });
      }
      if (delta.dirtyTokens.value.has(token)) {
        const result = await send(requests.update(token));
        if (result.ok) delta.dirtyTokens.value.delete(token);
        else errors.push({ token, messages: result.messages });
      }
    }
  };

  return async (): Promise<SaveError[]> => {
    const errors: SaveError[] = [];
    await destroyDeleted(errors);
    await saveInDocumentOrder(errors);
    return errors;
  };
};
